import { Modal } from "@portal/components/layout/Modal";
import { cn } from "@portal/components/lib/utils";
import { Button } from "@portal/components/ui/Button";
import { Card } from "@portal/components/ui/Card";
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { HiOutlineExclamationCircle } from "react-icons/hi2";
import {
	ErrorMessage,
	isAuthenticationError,
	logError,
	resolveErrorMessage,
	Severity,
} from "../utils/error";
import { isNetworkError } from "../utils/upload-error-messages";

export const GlobalErrorMessageModalContext = createContext<{
	triggerErrorModal: (err: Error, params?: Partial<ErrorMessage>) => void;
}>(undefined!);

type GlobalErrorMessageModalWrapperProps = {
	children: ReactNode;
};

export const GlobalErrorHandlingProvider = ({ children }: GlobalErrorMessageModalWrapperProps) => {
	const [error, setError] = useState<ErrorMessage | undefined>(undefined);
	const triggerErrorModal = useCallback(
		(err: Error, params?: Partial<ErrorMessage>) => {
			// Don't show the error modal for authentication errors
			if (isAuthenticationError(err)) {
				return;
			}

			// Network failures stay silent unless a caller opts in with message overrides
			// (upload flows). Background query/refetch failures should not spam the modal.
			if (isNetworkError(err) && !params) {
				return;
			}

			setError({
				...resolveErrorMessage(err),
				...params,
			});
		},
		[setError],
	);

	useEffect(() => {
		const handler = (event: ErrorEvent | PromiseRejectionEvent) => {
			event.preventDefault();
			const error = event instanceof ErrorEvent ? event.error : event.reason;
			if (!!error) {
				logError(error);
				triggerErrorModal(error);
			}
		};

		// Catch both sync and async errors at window level
		window.addEventListener("error", handler);
		window.addEventListener("unhandledrejection", handler);

		return () => {
			window.removeEventListener("error", handler);
			window.removeEventListener("unhandledrejection", handler);
		};
	}, [triggerErrorModal]);

	const severityStyles: Record<Severity, string> = {
		error: "border-red-500 bg-red-50 text-red-600/90",
		warn: "border-yellow-500 bg-yellow-50 text-yellow-600/90",
		info: "border-blue-500 bg-blue-50 text-link/90",
	};

	return (
		<>
			<GlobalErrorMessageModalContext.Provider value={{ triggerErrorModal }}>
				{children}
			</GlobalErrorMessageModalContext.Provider>
			{error && (
				<Modal
					onClickOutside={() => {
						error.onClose?.();
						setError(undefined);
					}}
				>
					<Card className="flex flex-col items-center gap-y-[24px] p-[24px] w-[500px] max-w-[500px]">
						<div
							className={cn(
								"flex space-x-[28px] w-full px-[28px] py-[22px] border border-opacity-40 rounded",
								severityStyles[error.severity || "error"],
							)}
						>
							<HiOutlineExclamationCircle className="text-[32px] stroke-[1.75] shrink-0 mt-[3px]" />
							<div className="flex flex-col space-y-[10px]">
								<span className="text-[20px] font-semibold">{error.header}</span>
								<span className="text-[15px] font-medium whitespace-pre-line">
									{<p>{error.description}</p>}
								</span>
							</div>
						</div>
						<Button
							onClick={() => {
								error.onClose?.();
								setError(undefined);
							}}
							color="secondary"
							className="w-1/2"
						>
							OK
						</Button>
					</Card>
				</Modal>
			)}
		</>
	);
};

export const useGlobalErrorMessageModal = () => useContext(GlobalErrorMessageModalContext);
